// Web-only "what to do with the captured PNG" step for the post-session
// share card (RW-18 follow-up, 2026-08-18). The capture itself is the shared
// <ShareCardRenderer>'s <ViewShot> (react-native-view-shot ships a real web
// backend, verified cross-platform — see packages/ui/src/Logger/
// ShareCardRenderer.tsx's header comment); this file is the one piece that
// genuinely differs per platform, same seam split as confirm.ts.
//
// Tries the Web Share API with a real file attachment first (native-feeling
// on the mobile browsers most likely to actually use this from a phone);
// falls back to a plain browser download when the API or file sharing isn't
// available (desktop Firefox/Safari today). Mirrors the Blob + createObjectURL
// + <a download> shape AccountScreen.tsx already uses for JSON export.

type ShareNavigator = Navigator & {
  share?: (data: { files?: File[]; title?: string }) => Promise<void>;
  canShare?: (data: { files?: File[] }) => boolean;
};

export async function shareOrDownloadPng(
  dataUrl: string,
  filename: string,
  title: string,
): Promise<void> {
  await shareOrDownloadPngs([dataUrl], [filename], title);
}

// A browser may hold back a burst of downloads: one at a time, spaced.
const DOWNLOAD_GAP_MS = 250;

/** Several PNGs (the report's pages) in one share when the browser takes
 *  files, else one download each. `filenames[i]` names `dataUrls[i]`. */
export async function shareOrDownloadPngs(
  dataUrls: readonly string[],
  filenames: readonly string[],
  title: string,
): Promise<void> {
  const blobs = await Promise.all(
    dataUrls.map(async (url) => (await fetch(url)).blob()),
  );
  const files = blobs.map(
    (blob, i) => new File([blob], filenames[i] ?? `image-${i + 1}.png`, { type: "image/png" }),
  );

  const nav = navigator as ShareNavigator;
  if (nav.share && nav.canShare?.({ files })) {
    try {
      await nav.share({ files, title });
      return;
    } catch (err) {
      // Drawing many pages can outlast the tap that asked for the share:
      // the browser then refuses it, and the pages download instead.
      if (!(err instanceof DOMException && err.name === "NotAllowedError")) throw err;
    }
  }

  for (const [i, file] of files.entries()) {
    if (i > 0) await new Promise((r) => setTimeout(r, DOWNLOAD_GAP_MS));
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  }
}
